import { Component, ElementRef, HostBinding, OnInit, ViewChild } from '@angular/core';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { Endpoint, HelpIndex, UpdateStatusType } from '../../../../models/enums';
import { AlignDirection, CellTemplateType, GenericColDef, GridContext, SortByFilter } from '../../../../models/grid-context';
import * as _ from 'underscore';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { ObjectActionListType, Permissions, ActionType } from '../../../../models/permission';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { Project, ProjectAction } from '../../../../models/project';

@Component({
    standalone: false,
    selector: 'res-delete-pending-projects',
    templateUrl: './delete-pending-projects.component.html',
    styleUrls: ['./delete-pending-projects.component.scss']
})
export class DeletePendingProjectsComponent implements OnInit {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    automationId = 'delete-pending-projects-list';
    gridName = 'Delete Pending Projects';
    isBusy = false;
    isBusyMessage = 'Loading Delete Pending Projects...';
    helpIndex = HelpIndex.sys_delete_pending_projects_dlg;
    updateStatusType = UpdateStatusType.PROJECT;
    isStorageDepotConfigured: boolean;
    sharedCorpora: any = [];

    // permissions
    hasDeletePendingProjectsAddEditPermissions: boolean;
    hasContextMenuPermissions = true;

    gridContext = new GridContext({
        sortTypeEnabled: true,
        sortEnabled: true,
        sortAscending: false,
        sortAscendingFieldName: 'descending',
        sortByFieldName: 'sortByFilter',
        sortBy: SortByFilter.DATE_CREATED
    });

    requestInfo = {
        endpoint: Endpoint.realmManager_listDeletePendingProjects,
        responseName: 'requests',
        data: {
            count: 100,
            startIndex: 0,
            descending: true,
            systemScope: true
        }
    };

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Project Name',
            field: 'objectName',
            automationId: 'project-name-column',
            lockVisible: true,
            minWidth: 60,
        },
        {
            headerName: 'Project Description',
            field: 'description',
            automationId: 'project-desc-column',
            minWidth: 60,
        },
        {
            headerName: 'Organization',
            field: 'orgName',
            automationId: 'organization-column',
            minWidth: 60,
        },
        {
            headerName: 'Request Status',
            field: 'requestStatus',
            automationId: 'request-status-column',
            minWidth: 60,
        },
        {
            headerName: 'Requested By',
            field: 'requestedBy',
            automationId: 'requested-by-column',
            minWidth: 60,
        },
        {
            headerName: 'Request Date',
            automationId: 'request-date-column',
            field: 'requestedOn',
            cellAlignDirection: AlignDirection.right,
            headerAlignDirection: AlignDirection.right,
            width: 145,
            minWidth: 145,
            sortBy: SortByFilter.DATE_CREATED
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        },
    );

    contextMenuOptions = [
        new ContextMenuOption({
            name: 'Approve Project Deletion',
            iconClass: 'fa-trash', iconColor: 'color-red-05',
            automationId: 'delete-pending-project-approve-project-deletion',
            click: (item) => this.approveProjectDeletion(item),
            enabled: () => this.isSystemStorageDepotEnabled(),
            tooltipFunction: () => this.isSystemStorageDepotEnabled() ? '' : 'A System Storage Depot must be set up before you can approve project deletion.'
        }),
        new ContextMenuOption({
            name: 'Decline Project Deletion', iconClass: 'fa-close',
            iconColor: 'color-grey-08', automationId: 'delete-pending-projects-list-decline-project-deletion',
            click: (item) => this.declineProjectDeletion(item)
        })
    ];

    constructor(private commonService: CommonService, private restService: RestService) {
    }

    ngOnInit(): void {
        this.getSystemStorageDepot();
    }

    getSystemStorageDepot(): void {
        this.restService.post(Endpoint.realmManager_getSystemStorageDepot, { systemScope: true }).then((storageDepotResponse) => {
            if (storageDepotResponse.systemStorageDepotDto?.depotId) {
                this.isStorageDepotConfigured = true;
            }
        });
    }

    isSystemStorageDepotEnabled(): boolean {
        return this.isStorageDepotConfigured;
    }

    selectedTreeItemChanged(node: GenericTreeNode): void {
        this.hasDeletePendingProjectsAddEditPermissions = Permissions.checkUserPermission(ObjectActionListType.DELETE_PENDING_PROJECTS, ActionType.EDIT);
        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (!this.hasDeletePendingProjectsAddEditPermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
                this.hasContextMenuPermissions = false;
            }
        }
        return;
    }

    approveProjectDeletion(item): void {
        const project = new Project({
            name: item?.objectName,
            handle: item?.objectHandle,
            orgName: item?.orgName,
            projectAction: ProjectAction.APPROVE_PROJECT_DELETION,
            projectHandle: item?.handle
        });
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: project }).then((result) => {
            if (result.modalResult) {
                setTimeout(() => {
                    this.genericGrid.loadData();
                }, 1000);
            }
        });
    }

    declineProjectDeletion(project): void {
        this.restService.post(Endpoint.adminOrgManager_declineProjectDeleteRequest, {
            handle: project.handle, systemScope: true, taskDescription: 'Declining delete for project ' + project.objectName
        }).then(result => {
            this.genericGrid.loadData();
        });
    }
}
